
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

interface Subcategory {
  id: string;
  name: string;
  slug: string;
  productCount?: number;
}

interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
  imageUrl?: string;
  productCount?: number;
  subcategories?: Subcategory[];
}

interface CategoryCardProps {
  category: Category;
  showSubcategories?: boolean;
}

const CategoryCard = ({ category, showSubcategories = false }: CategoryCardProps) => {
  const categorySlug = category.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and');
  
  return (
    <Card className="group hover:shadow-lg transition-shadow duration-200 overflow-hidden">
      <Link to={`/products?category=${categorySlug}`} className="block">
        <CardContent className="p-3 sm:p-4 text-center">
          {/* Small Round Thumbnail */}
          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-full overflow-hidden bg-gray-100">
            <img
              src={category.imageUrl || '/placeholder.svg'}
              alt={category.name}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-200"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/placeholder.svg';
              }}
              width={64}
              height={64}
              loading="lazy"
              decoding="async"
            />
          </div>
          
          {/* Category Name in White Block */}
          <div className="bg-white rounded-lg p-2 sm:p-3 shadow-sm border">
            <h3 className="font-playfair font-semibold text-gray-900 group-hover:text-springbok-600 transition-colors text-xs sm:text-sm leading-tight">
              {category.name}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {category.productCount !== undefined
                ? `${category.productCount} product${category.productCount !== 1 ? 's' : ''}`
                : ' '}
            </p>
          </div>
          
          {showSubcategories && category.subcategories && category.subcategories.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="space-y-1">
                {category.subcategories.slice(0, 3).map((sub) => (
                  <Link
                    key={sub.id}
                    to={`/products?category=${categorySlug}&subcategory=${sub.slug}`}
                    className="flex items-center justify-between text-xs text-gray-600 hover:text-springbok-600 transition-colors p-1 hover:bg-gray-50 rounded"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="truncate">{sub.name}</span>
                    <ChevronRight size={12} className="flex-shrink-0 ml-1" />
                  </Link>
                ))}
                {category.subcategories.length > 3 && (
                  <p className="text-xs text-gray-400 px-1">
                    +{category.subcategories.length - 3} more
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Link>
    </Card>
  );
};

export default CategoryCard;
